import * as _ from "lodash";
import * as angular from "angular";
import { User } from "../models/user.model";
import { OrganisationService } from "../services/organisation.service";
import { Filter } from "../models/filter.model";
import { OrganisationUserService } from "../services/organisation-user.service";

export class OrganisationUserDataProvider {
    private $q: any;
    private organisationService: OrganisationService;
    private service: OrganisationUserService;
    private $timeout: any;
    private $http: any;
    private localStorageService: any;
    private modelsUtils: any;

    public users: User[] = [];
    public searchPromise: any;
    public statsPromise: any;
    public stats = null;
    public customfieldsColumns = null;
    public userCustomFieldsColumnsPromise: any;
    public defaultColumns: Object = {
        name: { name: "Name", selected: true },
        email: { name: "Email", selected: true },
        phone: { name: "Phone Number", selected: true },
        permission: { name: "Permission", selected: true },
        campaigns: { name: "# of Accounts", selected: true },
        signedup: { name: "Signed Up", selected: true },
        lastlogin: { name: "Last Login", selected: true },
        logins: { name: "Login Sessions", selected: true },
        division: { name: "Division", selected: true },
        campaignname: { name: "Accounts", selected: true },
    };
    public scrollLoading: boolean;
    public loading: boolean;

    constructor(
        $q: any,
        $timeout: any,
        $http: any,
        OrganisationService: OrganisationService,
        OrganisationUserService: OrganisationUserService,
        localStorageService: any,
        ModelsUtils: any
    ) {
        this.$q = $q;
        this.organisationService = OrganisationService;
        this.service = OrganisationUserService;
        this.$timeout = $timeout;
        this.$http = $http;
        this.localStorageService = localStorageService;
        this.modelsUtils = ModelsUtils;
    }

    getColumns(): object {
        return _.merge(
            this.defaultColumns,
            this.localStorageService.get("organisationUserColumns")
        );
    }

    getAllUsers(
        filter: Filter,
        last?: number,
        scroll: boolean = false
    ): Promise<User[]> | Promise<string> {
        if (last && this.stats && this.users.length >= this.stats.total) {
            return Promise.resolve("Total users reached");
        }

        if (scroll) {
            this.scrollLoading = true;
        } else {
            this.loading = true;
        }
        const deferred = this.$q.defer();

        if (this.searchPromise) {
            this.searchPromise.resolve("user cancelled");
        }

        this.searchPromise = this.$q.defer();

        this.$http({
            url: "/v2/organisation/users",
            method: "GET",
            params: {
                filter: JSON.stringify(filter),
                last: last,
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                let json = response["data"];
                if (!last || last == 0) {
                    this.users = [];
                }
                if (json.data.length) {
                    for (let i = 0; i < json.data.length; i++) {
                        this.users.push(this.modelsUtils.user(json.data[i]));
                    }
                }
                if (scroll) {
                    this.scrollLoading = false;
                } else {
                    this.loading = false;
                }
                deferred.resolve(this.users);
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getUser(uid: string | number): Promise<User> {
        const deferred = this.$q.defer();

        this.service.info({ id: uid }).then((response: object) => {
            let json = response["data"];
            if (json.success) {
                deferred.resolve(this.modelsUtils.user(json.data));
            } else {
                deferred.reject();
            }
        });

        return deferred.promise;
    }

    getUserCampaigns(uid: string): Promise<object[]> {
        const deferred = this.$q.defer();
        this.$http({
            url: "/v2/organisation/user/campaigns",
            method: "GET",
            params: { uid: uid },
        }).then(
            (response: object) => deferred.resolve(response["data"].data),
            (error: object) => deferred.reject(error)
        );

        return deferred.promise;
    }

    clearAll(): void {
        this.users = null;
        this.customfieldsColumns = null;
        this.userCustomFieldsColumnsPromise = null;
    }

    headlineStats(): Promise<object[]> {
        if (this.stats !== null) {
            return this.$q.resolve(this.stats);
        } else if (this.statsPromise) {
            //Has been called already and has not resolved yet.
            return this.statsPromise;
        } else {
            const deferred = this.$q.defer();

            this.organisationService.userHeadlineStats().then(
                (response: object) => {
                    const json = response["data"];
                    this.stats = json.data;
                    deferred.resolve(this.stats);
                },
                (error: object) => deferred.reject(error)
            );

            this.statsPromise = deferred.promise;
            return this.statsPromise;
        }
    }

    userWithID(id: string, users?: Array<any>): any {
        let existingUser = null;
        angular.forEach(users, (user) => {
            if (user.uid == id) {
                existingUser = user;
            }
        });

        if (existingUser) {
            return existingUser;
        }
    }

    spliceUser(user: User): void {
        //Remove User from list
        let usersIndex = this.users.indexOf(user);
        if (usersIndex > -1) {
            this.users.splice(usersIndex, 1);
        }
    }

    deleteUser(user: User): Promise<object> {
        const deferred = this.$q.defer();
        this.organisationService.removeUser(user).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                this.spliceUser(user);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    replaceUser(user: User): void {
        this.$timeout(() => {
            let existingUser = this.userWithID(user.uid);
            if (existingUser) {
                angular.extend(existingUser, user);
            } else {
                this.users.unshift(user);
            }
        });
    }

    getCustomFieldColumns(): Promise<object[]> {
        const deferred = this.$q.defer();
        if (this.customfieldsColumns !== null) {
            deferred.resolve(this.customfieldsColumns);
            this.userCustomFieldsColumnsPromise = deferred.promise;
            return this.userCustomFieldsColumnsPromise;
        } else if (this.userCustomFieldsColumnsPromise) {
            return this.userCustomFieldsColumnsPromise;
        } else {
            this.organisationService.userCustomFields().then(
                (response) => {
                    const json = response["data"].data;
                    this.customfieldsColumns = [];
                    for (let i = 0; i < json.length; i++) {
                        this.customfieldsColumns.push(json[i]);
                    }
                    deferred.resolve(this.customfieldsColumns);
                },
                (error) => {
                    this.customfieldsColumns = [];
                    deferred.reject(error);
                }
            );
        }
        this.userCustomFieldsColumnsPromise = deferred.promise;
        return this.userCustomFieldsColumnsPromise;
    }

    processSocketUpdate(socketData: any): void {
        if (this.users) {
            this.replaceUser(this.modelsUtils.user(socketData));
        }
    }

    processSocketCreate(socketData: any): void {
        if (this.users) {
            this.replaceUser(this.modelsUtils.user(socketData));
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.users) {
            let existingUser = this.userWithID(socketData.uid);
            if (existingUser) {
                this.spliceUser(existingUser);
            }
        }
    }
}

angular
    .module("vcDataProvider")
    .service("OrganisationUserDataProvider", OrganisationUserDataProvider);
